<template>
	<div class="login-container">
		<div class="login-wrapper">
			<div class="login-heading">
				<a href="https://pronto.io" tabindex="-1">
					<ProntoNameLogoIcon class="name-logo" />
				</a>
			</div>

			<div class="login-card">
				<input
					v-if="historyStack.length"
					type="image"
					src="/images/common/full-arrow.png"
					class="back-button"
					@click="goBack"
					:alt="$t('common_button_back')"
					tabindex="0"
					autoFocus
				/>
				<div v-if="loginView === views.MAIN" class="main-card">
					<ProntoIcon class="pronto-logo" />
					<div class="title" v-text="$t('onboarding_loginmethod_title')" />
					<div
						class="subtitle"
						v-text="$t('onboarding_loginmethod_subtitle')"
					></div>
					<div>
						<Button large @click="changeView(views.EMAIL)" tabindex="0">
							{{ $t('onboarding_loginmethod_email_button') }}
						</Button>
						<Button large @click="changeView(views.PHONE)">
							{{ $t('onboarding_loginmethod_phone_button') }}
						</Button>
					</div>

					<a
						id="requestDemo"
						href="https://support.pronto.io/en/articles/3273143-how-do-i-sign-into-pronto"
						target="_blank"
					>
						{{ $t('onboarding_loginmethod_moreinfo_button') }}
					</a>

					<RouterLink class="invite-link-footer" :to="{ name: 'code' }">
						{{ $t('groupinvites_have_invite_link') }}
					</RouterLink>
				</div>

				<ActiveAccounts
					v-if="loginView === views.ACTIVE_ACCOUNTS"
					:accounts="accounts"
					@another-account="changeView(views.MAIN)"
					@refresh="refreshAccounts"
				/>

				<div v-if="loginView === views.PHONE" class="phone-card">
					<div class="card-heading">
						{{ $t('onboarding_phone_prompt') }}
					</div>

					<FormItem
						:label="$t('onboarding_loginmethod_phone_button')"
						:error="missingPhone"
						:message="$t('onboarding_missing_phone_error')"
					>
						<PhoneInput
							v-model="phone"
							@keyup.enter="verifySubmit()"
							autoFocus
							large
						/>
					</FormItem>

					<div
						class="terms-and-service"
						v-html="
							$t('onboarding_phone_termsofservice', {
								TermsUrl: 'https://www.pronto.io/user-terms',
								PrivacyUrl: 'https://www.pronto.io/privacy',
							})
						"
					/>

					<Button
						pronto
						large
						block
						:disabled="disabled"
						@click="verifySubmit()"
					>
						{{ $t('onboarding_send_code_button') }}
					</Button>
					<Button
						pronto
						link
						@click.prevent="changeView(views.EMAIL)"
						tabindex="0"
					>
						{{ $t('onboarding_phone_switchmode_phone') }}
					</Button>
				</div>
				<div v-if="loginView === views.EMAIL" class="email-card">
					<div class="card-heading">
						{{ $t('onboarding_email_prompt') }}
					</div>

					<FormItem
						:label="$t('onboarding_loginmethod_email_button')"
						:error="missingEmail"
						:message="$t('onboarding_missing_email_error')"
					>
						<Input
							large
							autoFocus
							v-model="email"
							placeholder="john@pronto.io"
							type="email"
							@enter="verifySubmit"
						/>
					</FormItem>

					<div
						style="margin-top: 15px;"
						class="terms-and-service"
						v-html="
							$t('onboarding_phone_termsofservice', {
								TermsUrl: 'https://www.pronto.io/user-terms',
								PrivacyUrl: 'https://www.pronto.io/privacy',
							})
						"
					/>

					<Button
						pronto
						large
						block
						:disabled="disabled"
						@click="verifySubmit()"
					>
						{{ $t('onboarding_send_code_button') }}
					</Button>
					<Button
						pronto
						link
						@click.prevent="changeView(views.PHONE)"
						tabindex="0"
					>
						{{ $t('onboarding_phone_switchmode_email') }}
					</Button>
				</div>

				<div
					v-if="loginView === views.LOGIN_ACCOUNTS"
					style="min-height: 400px;"
				>
					<LoginAccounts
						v-if="users && !loading"
						:accounts="users"
						:contact="email || phone"
						@account="chooseAccount"
					/>
				</div>

				<div v-if="loginView === views.CODE" class="code-card">
					<div class="card-heading">
						{{ $t('onboarding_verification_sent', { EmailPhone: '' }) }}
						<div class="contact" v-text="phone || email" />
					</div>

					<FormItem
						:label="$t('onboarding_verify_input_placeholder')"
						:error="missingCode"
						:message="$t('onboarding_missing_code_error')"
					>
						<Input
							large
							autoFocus
							@enter="loginSubmit()"
							v-model="code"
							:disabled="loading"
						/>
					</FormItem>

					<Checkbox
						v-if="!isDesktop"
						v-model="persistChecked"
						pronto
						@enter="persistChecked = !persistChecked"
					>
						{{ $t('onboarding_keep_me_signed_in') }}
					</Checkbox>

					<Button
						pronto
						large
						block
						:loading="loading"
						:disabled="disabled"
						@click="loginSubmit()"
						@keyup.enter="loginSubmit()"
						class="sign-in-button"
					>
						{{ $t('onboarding_splash_signin') }}
					</Button>
				</div>
			</div>
		</div>

		<SimpleModal v-if="tryAgain" @close="tryAgain = false">
			<div style="padding: 50px;">
				<div class="upload-title" style="margin-top: 0; margin-bottom: 0;">
					😢
				</div>
				<div class="upload-title font18" style="margin-top: 0;">
					{{
						email
							? $t('apierrors_EMAIL_NOT_FOUND')
							: $t('apierrors_PHONE_NOT_FOUND')
					}}
				</div>
				<div class="center">
					<button class="btnActive" @click="tryAlternate">
						{{
							email
								? $t('onboarding_login_tryphone')
								: $t('onboarding_login_tryemail')
						}}
					</button>
				</div>
			</div>
		</SimpleModal>

		<LoggedOutModal
			v-if="loggedOutOrg"
			:org="loggedOutOrg"
			@signin="onSignIn"
			@cancel="loggedOutOrg = null"
		/>
	</div>
</template>

<script>
import { post } from '@/helpers/api'
import store from '@/store'
import {
	SimpleModal,
	Button,
	FormItem,
	Input,
	PhoneInput,
	Checkbox,
} from '@/components/common'

import {
	redirectToAccount,
	redirectToAccountInNewTab,
	getDeviceInfo,
} from '@/helperMethods'
import ActiveAccounts from './ActiveAccounts'
import LoginAccounts from './LoginAccounts'
import LoggedOutModal from './LoggedOutModal'
import { ProntoNameLogoIcon, ProntoIcon } from '@/icons'
import parsePhoneNumberFromString from 'libphonenumber-js'

const views = {
	MAIN: 'MAIN',
	LOGIN_ACCOUNTS: 'LOGIN_ACCOUNTS',
	ACTIVE_ACCOUNTS: 'ACTIVE_ACCOUNTS',
	PHONE: 'PHONE',
	EMAIL: 'EMAIL',
	CODE: 'CODE',
}

export default {
	components: {
		SimpleModal,
		Checkbox,
		ActiveAccounts,
		LoginAccounts,
		ProntoNameLogoIcon,
		ProntoIcon,
		Button,
		FormItem,
		Input,
		PhoneInput,
		LoggedOutModal,
	},

	data() {
		return {
			store,
			views,
			user_id: null,
			users: null,
			historyStack: [],
			code: null,
			currentMethod: '',
			disabled: false,
			email: null,
			loading: false,
			missingCode: false,
			missingEmail: false,
			missingPhone: false,
			persistChecked: false,
			phone: null,
			loginView: views.MAIN,
			tryAgain: false,
			loggedOutOrg: null,
			accounts: store
				.getActiveUsers()
				.sort((a, b) => a.organization.name.localeCompare(b.organization.name)),
		}
	},

	mounted() {
		this.$nextTick(() => {
			if (store.getActiveUsers().length) {
				this.loginView = views.ACTIVE_ACCOUNTS
			}
		})

		this.checkIfLoggedOut()
	},

	watch: {
		phone(val) {
			if (val) {
				this.missingPhone = false
			}
		},

		email(val) {
			if (val) {
				this.missingEmail = false
			}
		},

		code(val) {
			if (val) {
				this.missingCode = false
			}
		},

		loginView(view, prev) {
			if (view === views.EMAIL) {
				this.phone = null
			} else if (view === views.PHONE) {
				this.email = null
			}
			if (prev === views.EMAIL) {
				this.missingEmail = false
			} else if (prev === views.PHONE) {
				this.missingPhone = false
			} else if (prev === views.LOGIN_ACCOUNTS) {
				this.users = null
			}
			this.code = null
			this.disabled = false
			this.showErrors = false
			this.tryAgain = false
		},
	},

	computed: {
		isDesktop() {
			return window.todesktop ? true : false
		},
	},

	methods: {
		refreshAccounts() {
			this.accounts = store
				.getActiveUsers()
				.sort((a, b) => a.organization.name.localeCompare(b.organization.name))
		},
		changeView(view) {
			this.historyStack.push(this.loginView)
			this.loginView = view
		},

		goBack() {
			this.loginView = this.historyStack.pop() || views.MAIN
		},

		isCurrentView(view) {
			return this.loginView === view
		},

		tryAlternate() {
			this.changeView(
				this.loginView === views.PHONE ? views.EMAIL : views.PHONE
			)
		},

		checkIfLoggedOut() {
			const { loggedout, org, ...rest } = this.$route.query
			if (loggedout) {
				this.loggedOutOrg = org
				this.$router.replace({ query: rest })
			}
		},

		verifySubmit() {
			switch (this.loginView) {
				case views.PHONE:
					if (!this.phone) {
						this.missingPhone = true
						return
					}
					break
				case views.EMAIL:
					if (!this.email) {
						this.missingEmail = true
						return
					}
					break
			}

			this.loading = true
			this.disabled = true

			let body = { email: this.email }
			if (this.isCurrentView(views.PHONE)) {
				const { number, country } = parsePhoneNumberFromString(this.phone)
				body = { phone: number, phone_country: country }
			}

			post('/api/v1/user.verify', body)
				.then(({ data }) => {
					if (data.ok) {
						this.loading = false
						this.disabled = false
						return this.changeView(views.CODE)
					}
					switch (data.error) {
						case 'PHONE_NOT_FOUND':
							this.currentMethod = 'phone number'
							this.tryAgain = true
							break

						case 'EMAIL_NOT_FOUND':
							this.currentMethod = 'email address'
							this.tryAgain = true
							break

						case 'NO_ORGS_FOR_USER':
							if (this.loginView === 'PHONE') {
								this.currentMethod = 'phone number'
								this.tryAgain = true
							} else {
								this.currentMethod = 'email address'
								this.tryAgain = true
							}
							break

						case 'SEND_LIMIT_REACHED':
						case 'INVALID_PHONE_PHONE':
							this.$alert.error(this.$t(`apierrors_${data.error}`))
							break

						default:
							this.$alert.error(this.$t('apierrors_error_generic'))
							break
					}

					this.disabled = false
					this.loading = false
				})
				.catch((error) => {
					console.error(error)
					this.$alert.error()
					this.loading = false
					this.disabled = false
				})
		},

		chooseAccount(account) {
			this.user_id = account.user.id
			this.tokenLaunch(account)
		},

		async loginSubmit() {
			if (!this.code) {
				this.missingCode = true
				return
			}

			this.disabled = true

			let body = { email: this.email, code: this.code }
			if (this.phone) {
				const { number, country } = parsePhoneNumberFromString(this.phone)
				body = { phone: number, phone_country: country, code: this.code }
			}

			if (this.user_id) {
				body.user_id = this.user_id
			}

			body.device = getDeviceInfo()

			this.loading = true
			let errorCode = null

			try {
				const { data } = await post('/api/v3/user.login', body)
				if (!data.ok) {
					errorCode = data.error
					throw new Error(errorCode)
				}

				if (data.users.length === 1) {
					this.tokenLaunch(data.users[0])
					return
				}

				this.users = data.users
				this.changeView(views.LOGIN_ACCOUNTS)
				this.loading = false
			} catch (e) {
				console.error(e)
				errorCode = errorCode || 'INCORRECT_CODE'
				this.$alert.error(this.$t(`apierrors_${errorCode}`))

				this.showErrors = true
				this.disabled = false
				this.loading = false
			}
		},

		tokenLaunch({ user, logintoken }, inNewTab) {
			// get link and all necessary params
			const persist = this.isDesktop ? true : this.persistChecked
			if (inNewTab) {
				redirectToAccountInNewTab(user, logintoken, { persist })
			} else {
				redirectToAccount(user, logintoken, { persist })
			}
		},

		onSignIn() {
			this.loggedOutOrg = null
			this.changeView(views.MAIN)
		},
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

:deep(.terms-and-service) {
	width: 100%;
	margin: 12px 0 60px 0;
	text-align: left;
	a {
		text-decoration: none;
		color: var(--teal-300);
	}
}

.login-container {
	width: 100%;
	height: 100vh;
	overflow-y: scroll;
	background-color: var(--teal-300);
	background-image: url('/images/pronto/hills.svg');
	background-position: 0 100%;
	background-repeat: repeat-x;

	.main-view {
		display: flex;
		align-items: center;
		height: 100vh;
	}

	.back-button {
		margin-top: -10px;
		outline: 0;
	}

	.title {
		@include fontFamily(500);
		font-size: 28px;
		color: var(--neutral-800);
		margin-bottom: 45px;
	}

	.subtitle {
		@include fontFamily(500);
		font-size: 18px;
		color: $neutral-500;
		margin-bottom: 45px;
	}

	.pronto-logo {
		font-size: 59px;

		:deep(svg) path {
			fill: var(--teal-300);
		}
	}

	.name-logo {
		display: block;
		margin-top: 32px;
		display: unset;

		:deep(svg:not(.backdrop)) {
			width: unset;
		}
	}

	.pronto-btn + .pronto-btn:not(.link) {
		// if putting buttons in a row, add extra spacing between them
		margin-left: 24px;
	}

	.login-wrapper {
		@include flex-column;
	}
}

.login-heading {
	font-family: Avenir-Medium;
	font-size: 70px;
	text-align: center;
	color: var(--neutral-0);
	font-weight: 300;
	margin-bottom: 60px;
	margin-left: -22px;
}

.login-heading a,
.login-heading :deep(svg) {
	cursor: pointer;
	outline: 0;
	width: 230px;
	margin-top: 80px;
	color: var(--neutral-0);
}

.login-card {
	width: 584px;
	border-radius: 6px;
	background-color: #ffffff;
	margin: 0 auto 20px;
	padding: 40px;
	min-height: 500px;
	position: relative;
	> div {
		@include flex-column-center;
		height: 100%;
	}
	.pronto-btn.block {
		margin-top: 16px;
	}
	.email-card,
	.phone-card {
		margin: 20px 0 -20px 0;
		.card-heading {
			margin-bottom: 64px;
		}
	}
	.pronto-checkbox {
		margin-top: 8px;
		width: 100%;
	}
	.sign-in-button {
		margin-top: 125px !important;
	}
	.card-heading {
		font-size: 28px;
		color: var(--neutral-800);
		margin-bottom: 40px;
		text-align: center;
	}
}

.main-card {
	text-align: center;
	> .subtitle {
		max-width: 380px;
	}
	#requestDemo {
		margin: 16px 0 32px 0;
	}
}

.login-banner-img {
	width: 100%;
}

#requestDemo {
	color: var(--teal-300);
	font-family: Avenir-Book;
	font-size: 15px;
	text-decoration: none;
}

.users-selection {
	width: 500px;
	height: 450px;
	margin: 0 auto;
	overflow-y: auto;
}

.user {
	display: block;
	width: 100%;
	height: 90px;
	padding: 20px;
	border: 1px solid #dadada;
	border-radius: 10px;
	margin-bottom: 10px;
	cursor: pointer;
	position: relative;
}

.profilepic {
	width: 60px;
	height: 60px;
	position: absolute;
	left: 20px;
	top: 15px;
}

.profilepic-img {
	width: 80px;
	height: 80px;
	margin-right: 30px;
	vertical-align: middle;
	-o-object-fit: cover;
	object-fit: cover;
}

.account-info {
	width: 380px;
	display: inline-block;
	position: absolute;
	left: 100px;
	top: 16px;
}

.user-name {
	width: 380px;
	font-family: Avenir;
	font-size: 18px;
	overflow: hidden;
	text-overflow: ellipsis;
	max-height: 50px;
}

.user-account {
	font-family: Avenir;
	color: #b7b9bd;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.login-loader {
	width: 80px;
	height: 80px;
	-o-object-fit: cover;
	object-fit: cover;
	position: absolute;
	left: 255px;
	top: 60px;
}

.normal-select .vs__selected {
	color: #8b8d93;
}

.normal-select .vs__dropdown-option--highlight {
	background: #10a5c8 !important;
}

#countryCodeOption {
	color: #8b8d93;
}

.switch-login-method {
	text-align: center;
	height: 60px;
	padding-top: 20px;
}

.keep-me-signedin {
	padding-left: 51px;
	height: 25px;
}

.m-chckbox--container {
	margin: 0;
}

.error-text-span {
	position: absolute;
	right: 56px;
}

.sign-in-container {
	text-align: center;
	margin-top: 140px;
}

:deep(.m-chckbox--label) {
	padding-left: 8.5px;
}

:deep(.m-chckbox--group) {
	border: 2px solid #dfe1e3;
}

.main-wrapper {
	display: flex;
	.account-container {
		text-align: left;
		width: 50%;
		:deep(.org-picker) {
			width: 280px;
		}
	}
	.main-divider {
		height: 250px;
		border-right: 1px solid #dfe1e3;
	}
	.main-container {
		width: 340px;
		margin: 0 auto 32px auto;
	}
}

.phone-submit {
	width: 100%;
}

@media screen and (max-width: 800px) {
	.main-card {
		margin-top: 20px !important;
		padding-top: 15px !important;
		position: relative;

		img {
			margin-bottom: 15px !important;
		}

		.button-group {
			margin-top: 30px !important;
			margin-bottom: 35px !important;
		}
	}

	.login-heading {
		display: none !important;
	}

	.login-card {
		width: 504px !important;
		height: 500px !important;
		margin-top: 20px !important;
	}

	.login-email-input,
	.login-phone-input,
	.login-code-input {
		width: 410px;
	}

	#countryCodeOption div:first-child {
		width: unset !important;
	}

	#login {
		bottom: 70px !important;
	}

	#login-email-control {
		margin-top: 50px !important;
	}

	#login-phone-control {
		margin-top: 15px !important;
	}
}

.invite-link-footer {
	height: 54px;
	background: #f3f4f5;
	border-radius: 0px 0px 6px 6px;
	margin-top: 44px;
	font-size: 16px;
	color: var(--neutral-400);
	@include flex-center;
	position: absolute;
	right: 0;
	left: 0;
	bottom: 0;
}

.form-group {
	width: 100%;
	margin-left: 0;
	padding: 0 50px;
}
</style>

<style lang="scss">
@import 'src/sass/variables';
@include overrideScheme('dark') {
	.login-container {
		background: var(--neutral-0);
		.login-card {
			background-color: var(--neutral-300);
			.pronto-input {
				background: var(--neutral-100);
			}
		}
	}

	.login-heading a,
	.login-heading svg {
		color: var(--teal-300);
	}

	.main-card {
		.pronto-btn {
			background: var(--neutral-100);
			box-shadow: var(--neutral-200);
			&:hover {
				background: var(--neutral-200) !important;
			}
		}
		.invite-link-footer {
			background: var(--neutral-100);
			color: var(--neutral-600);
		}
	}
}
</style>
